import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { ChatIcon, DeleteIcon, EditPenIcon, EmailIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { getDiscussChatMenuTargets } from 'app/shared/components/TeamChat/TeamChat.discussMenu.utils';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PendingChangesActionType } from 'constants/pendingChanges';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { selectEntityReferenceLabel } from 'store/loanApplications/selectors';
import { requestDiscussEntityInChatAction } from 'store/teamChat/actions';

import {
  EDIT_ENTITY_DEFAULT_SECTION,
  ENTITY_TYPE_HREF,
  REMOVE_ENTITY_BUTTON_TEXT,
} from '../EditEntityModal/EditEntityModal.constants';
import InviteCoBorrowerDrawer from '../InviteCoBorrowerDrawer';

import {
  DELETE_ENTITY_CONTENT_CONFIG,
  INVITE_CO_BORROWER_MENU_LABEL,
} from './ActionButton.constants';

const defaultObject = {};

const ActionButton = ({
  rowData,
  type,
  pageData = defaultObject,
  hasPosDomain = false,
  setIsLoading,
}) => {
  const dispatch = useDispatch();

  const history = useHistory();
  const { pathname } = useLocation();

  const { applicationId, tab, chatTargets } = pageData || {};
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isBorrower = type === ENTITY_TYPE_HREF.BORROWER;
  const entityLabel = useSelector(
    selectEntityReferenceLabel({
      entityType: isBorrower ? ENTITY_REFERENCE_TYPE.BORROWER : null,
      entityId: rowData.id,
    }),
  );

  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);

  const isNewAdded = rowData.pendingChangesActionType === PendingChangesActionType.ADDED;
  const canInviteCoBorrower = isBorrower && hasPosDomain && !rowData.isPrimary;
  const discussTargets = useMemo(
    () => (isBorrower && chatTargets ? getDiscussChatMenuTargets(chatTargets) : []),
    [isBorrower, chatTargets],
  );

  const onClickEdit = useCallback(() => {
    onClearAnchorElement();

    const path = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
      subtab: type,
      entityId: rowData.id,
      section: EDIT_ENTITY_DEFAULT_SECTION[type],
      pathFrom: pathname,
    });

    history.push(path);
  }, [applicationId, tab, rowData, type, pathname, history, onClearAnchorElement]);

  const onClickRemove = useCallback(() => {
    const { modalMessage, modalDescription, action, getParams, successMessage } =
      DELETE_ENTITY_CONTENT_CONFIG[type];
    const params = getParams({ applicationId, entityId: rowData.id });

    onClearAnchorElement();

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: modalMessage,
        description: modalDescription,
        onAccept: () => {
          setIsLoading(true);

          dispatch(action(params))
            .then(() => {
              dispatch(
                showMessageAction({
                  message: successMessage,
                  variant: 'success',
                }),
              );
            })
            .catch(Error)
            .finally(() => setIsLoading(false));
        },
      }),
    );
  }, [applicationId, dispatch, rowData.id, setIsLoading, type, onClearAnchorElement]);

  const onClickInvite = useCallback(() => {
    onClearAnchorElement();
    setIsInviteDrawerOpen(true);
  }, [onClearAnchorElement]);

  const onInviteDrawerClose = useCallback(() => setIsInviteDrawerOpen(false), []);

  const onClickDiscuss = useCallback(
    chatId => {
      onClearAnchorElement();

      dispatch(
        requestDiscussEntityInChatAction({
          chatId,
          entityType: ENTITY_REFERENCE_TYPE.BORROWER,
          entityId: rowData.id,
          label: entityLabel,
        }),
      );
    },
    [dispatch, rowData.id, entityLabel, onClearAnchorElement],
  );

  const items = useMemo(
    () => [
      { text: 'Edit', onClick: onClickEdit, icon: <EditPenIcon size={IconSize[20]} /> },
      ...getConditionalArray(canInviteCoBorrower, () => [
        {
          text: INVITE_CO_BORROWER_MENU_LABEL,
          onClick: onClickInvite,
          icon: <EmailIcon size={IconSize[20]} />,
        },
      ]),
      ...discussTargets.map(({ chatId, text }) => ({
        text,
        onClick: () => onClickDiscuss(chatId),
        icon: <ChatIcon size={IconSize[20]} />,
      })),
      {
        text: `Remove ${REMOVE_ENTITY_BUTTON_TEXT[type]}`,
        onClick: onClickRemove,
        icon: <DeleteIcon size={IconSize[20]} />,
        isDisabled: rowData.isPrimary,
      },
    ],
    [
      onClickEdit,
      onClickRemove,
      onClickInvite,
      onClickDiscuss,
      canInviteCoBorrower,
      discussTargets,
      type,
      rowData,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        isHighlighted={isNewAdded}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />

      {canInviteCoBorrower && (
        <InviteCoBorrowerDrawer
          applicationId={applicationId}
          borrower={rowData}
          open={isInviteDrawerOpen}
          onClose={onInviteDrawerClose}
        />
      )}
    </>
  );
};

ActionButton.propTypes = {
  rowData: PropTypes.object.isRequired,
  type: PropTypes.oneOf(Object.values(ENTITY_TYPE_HREF)).isRequired,
  pageData: PropTypes.object,
  hasPosDomain: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default withLoading(ActionButton);
